import { Link } from 'react-router-dom';
import {
  CreditCard,
  Shield,
  CheckCircle,
  Building2,
  Users,
  DollarSign,
  X,
  Landmark,
} from 'lucide-react';
import type { ManagedStripeEntity, StripeConnectScope } from '../../lib/stripeConnect';
import { StripeConnectMethodPicker } from './StripeConnectMethodPicker';

interface StripeConnectOnboardingModalProps {
  scope: StripeConnectScope;
  entityId: string;
  entityName: string;
  sharedPayoutHint?: string | null;
  linkableSource?: (ManagedStripeEntity & { account: { stripe_account_id: string | null } }) | null;
  onClose: (mode: 'later' | 'permanent') => void;
  onConnected?: () => void;
}

const steps = [
  {
    icon: CreditCard,
    title: 'Connect Stripe',
    description: 'Link or create a Stripe account — handled securely by Stripe.',
  },
  {
    icon: Shield,
    title: 'Verify your details',
    description: 'Add business info and a bank account for payouts (about 10 minutes).',
  },
  {
    icon: DollarSign,
    title: 'Accept payments',
    description: 'Collect registration fees, team dues, and store orders through Podium HQ.',
  },
];

export function StripeConnectOnboardingModal({
  scope,
  entityId,
  entityName,
  sharedPayoutHint,
  linkableSource,
  onClose,
  onConnected,
}: StripeConnectOnboardingModalProps) {
  const isTeam = scope === 'team';
  const Icon = isTeam ? Users : Building2;
  const paymentsPath = isTeam ? '/team-payments' : '/admin/organizations';
  const paymentsLabel = isTeam ? 'Team Payments' : 'Organizations';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />

      <div className="relative w-full max-w-lg max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col my-auto border border-gray-200 dark:border-slate-700">
        <div className="absolute top-0 left-0 right-0 h-36 bg-gradient-to-br from-purple-600 via-violet-600 to-teal-600 opacity-95" />

        <button
          type="button"
          onClick={() => onClose('later')}
          className="absolute top-4 right-4 z-10 p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative px-6 pt-8 pb-4 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white dark:bg-slate-800 rounded-xl shadow-lg mb-4">
            <Icon className="w-7 h-7 text-purple-600" />
          </div>
          <h2 className="text-xl font-bold text-white mb-1">Set up payments</h2>
          <p className="text-sm text-white/90">
            Connect <span className="font-semibold">{entityName}</span>&apos;s own Stripe account to accept
            fees online. Each team or org uses a separate Stripe account — not the Podium HQ platform account.
          </p>
        </div>

        <div className="relative px-6 pb-6 overflow-y-auto flex-1">
          <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-3 mb-4">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div key={step.title} className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 flex items-center justify-center">
                    <StepIcon className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {index + 1}. {step.title}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-slate-400">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-slate-400 bg-teal-50 dark:bg-teal-500/10 border border-teal-100 dark:border-teal-500/20 rounded-lg px-3 py-2 mb-4">
            <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
            <span>
              Funds go directly to your Stripe account. Podium HQ never stores card or bank details.
            </span>
          </div>

          {sharedPayoutHint && (
            <div className="flex items-start gap-2 text-xs text-purple-800 dark:text-purple-200 bg-purple-50 dark:bg-purple-500/10 border border-purple-100 dark:border-purple-500/20 rounded-lg px-3 py-2 mb-4">
              <Landmark className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>{sharedPayoutHint}</span>
            </div>
          )}

          <StripeConnectMethodPicker
            scope={scope}
            entityId={entityId}
            linkableSource={linkableSource}
            onLinked={() => {
              onConnected?.();
              onClose('later');
            }}
          />

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
            <button
              type="button"
              onClick={() => onClose('later')}
              className="text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
            >
              Remind me later
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => onClose('permanent')}
                className="text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
              >
                Skip for now
              </button>
              <Link
                to={paymentsPath}
                onClick={() => onClose('later')}
                className="text-sm font-medium text-purple-600 hover:text-purple-700"
              >
                {paymentsLabel} →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
